import { Theme } from '@eversports/react-components/design-tokens/theme'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import React from 'react'
import { useLocation } from 'react-router-dom'

import { motion } from '@eversports/react-components/design-tokens/motion'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import KlimtHeading from '@eversports/klimt-primitives/legacy/Heading'
import KlimtText from '@eversports/klimt-primitives/legacy/Text'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Page from '../../boilerplate/Page'
import Venue from '../../typings/Venue'
import Customer from '../../typings/Customer'
import useCheckoutContext from '../../data/useCheckoutContext'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import AppPromotion from '../AppPromotion'
import { DesktopBookingDetails, MobileBookingDetails } from '../BookingDetails'
import BottomFixedCollapsibleSection from '../BottomFixedCollapsibleSection'

import ConfirmationActions from './ConfirmationActions'
import ConfirmationHeading from './ConfirmationHeading'
import ConfirmationSubHeading from './ConfirmationSubHeading'
import MobileConfirmationCollapsibleCardHeader from './MobileConfirmationCollapsibleCardHeader'
import NewsletterBanner from './NewsletterBanner'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 840,
  },
  heading: {
    [theme.breakpoints.up('sm')]: {
      textTransform: 'uppercase',
    },
  },
  subHeading: {
    maxWidth: 308,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 550,
    },
  },
  hiddenInMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  visibleInMobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
}))

interface Props {
  venue: Venue
  eventId?: string
  isOrderConfirmed: boolean
  hasBooking: boolean
  hasSelfCheckIn: boolean
  invoiceDownloadLink?: string | null
  wasSomethingPayed: boolean
  voucherCode?: string
  customer: Customer | null
}

const ConfirmationComponent: React.FC<React.PropsWithChildren<Props>> = ({
  venue,
  eventId,
  hasBooking,
  isOrderConfirmed,
  hasSelfCheckIn,
  invoiceDownloadLink,
  wasSomethingPayed,
  voucherCode,
  customer,
}) => {
  const classes = useStyles()
  const location = useLocation()
  const { isVenueBrandingEnabled } = useCheckoutContext()
  const { hideEversportsBranding } = getPhoenixUrlParams(location.search)

  if (isVenueBrandingEnabled || hideEversportsBranding) {
    return (
      <Stack sx={{ marginX: 'auto', marginY: { xs: 10, sm: 0 }, width: '100%', alignItems: 'center' }}>
        <Stack
          sx={{ justifyContent: 'center', width: '100%', maxWidth: '840px', padding: 2 }}
          data-testid="confirmation-page"
        >
          <motion.div
            initial={{ opacity: 0, y: '-100vh' }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeInOut' }}
          >
            <Stack sx={{ alignItems: 'center', gap: 3, marginY: 4 }}>
              <motion.span
                initial={{ scale: 0.25, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, transition: { ease: 'easeInOut', duration: 0.5, delay: 0.25 } }}
              >
                <Icon icon={Icons.SUCCESS} size="xxx-large" color="black" />
              </motion.span>
              <KlimtHeading
                is="h1"
                variant="large"
                sx={{ textAlign: 'center', paddingX: 3, textTransform: { sm: 'uppercase' } }}
              >
                <ConfirmationHeading
                  isOrderConfirmed={isOrderConfirmed}
                  hasBooking={hasBooking}
                  wasSomethingPayed={wasSomethingPayed}
                  venue={venue.name}
                />
              </KlimtHeading>
              <KlimtText
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  marginBottom: 2,
                  maxWidth: { xs: '308px', sm: '550px' },
                }}
              >
                <ConfirmationSubHeading
                  isOrderConfirmed={isOrderConfirmed}
                  hasBooking={hasBooking}
                  hasSelfCheckIn={hasSelfCheckIn}
                />
              </KlimtText>
            </Stack>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: '200vh' }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeInOut' }}
          >
            <Stack
              sx={{ gap: 4, maxWidth: { xs: '360px', md: '100%' }, margin: '0 auto', paddingBottom: { xs: 10, sm: 0 } }}
            >
              <ConfirmationActions
                venue={venue}
                eventId={eventId}
                hasBooking={hasBooking}
                invoiceDownloadLink={invoiceDownloadLink}
                voucherCode={voucherCode}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <DesktopBookingDetails />
              </Box>
              {customer && !customer.isSubscribedToNewsletter && (
                <NewsletterBanner venue={venue} customerId={customer.id} />
              )}
            </Stack>
          </motion.div>
        </Stack>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <BottomFixedCollapsibleSection
            collapsibleContent={<MobileBookingDetails />}
            showExpandIcon
            hideHeaderOnExpand
          >
            <Text textAlign="center" fontWeight="bold" paddingTop="default">
              <MobileConfirmationCollapsibleCardHeader hasBooking={hasBooking} />
            </Text>
          </BottomFixedCollapsibleSection>
        </Box>
      </Stack>
    )
  }

  return (
    <Page>
      <Flex justifyContent="center" marginBottom="hero" fullWidth data-testid="confirmation-page">
        <Flex flexDirection="column" className={classes.container}>
          <motion.div
            initial={{ opacity: 0, y: '-100vh' }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeInOut' }}
          >
            <Flex
              alignItems="center"
              flexDirection="column"
              verticalSpacing="wide"
              marginTop="wide"
              marginBottom="wide"
            >
              <motion.span
                initial={{ scale: 0.25, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, transition: { ease: 'easeInOut', duration: 0.5, delay: 0.25 } }}
              >
                <Icon icon={Icons.SUCCESS} size="xxx-large" color="primary.main" />
              </motion.span>
              <Heading is="h1" variant="large" textAlign="center" className={classes.heading}>
                <ConfirmationHeading
                  isOrderConfirmed={isOrderConfirmed}
                  hasBooking={hasBooking}
                  wasSomethingPayed={wasSomethingPayed}
                  venue={venue.name}
                />
              </Heading>
              <Text textAlign="center" fontWeight="bold" marginBottom="wide" className={classes.subHeading}>
                <ConfirmationSubHeading
                  isOrderConfirmed={isOrderConfirmed}
                  hasBooking={hasBooking}
                  hasSelfCheckIn={hasSelfCheckIn}
                />
              </Text>
            </Flex>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: '200vh' }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeInOut' }}
          >
            <Flex flexDirection="column" verticalSpacing="wide">
              <ConfirmationActions
                venue={venue}
                eventId={eventId}
                hasBooking={hasBooking}
                invoiceDownloadLink={invoiceDownloadLink}
                voucherCode={voucherCode}
              />
              <div className={classes.hiddenInMobile}>
                <DesktopBookingDetails />
              </div>
              {customer && !customer.isSubscribedToNewsletter && (
                <NewsletterBanner venue={venue} customerId={customer.id} />
              )}
              <AppPromotion venue={venue} hasSelfCheckIn={hasSelfCheckIn} />
            </Flex>
          </motion.div>
        </Flex>
      </Flex>
      <div className={classes.visibleInMobile}>
        <BottomFixedCollapsibleSection collapsibleContent={<MobileBookingDetails />} showExpandIcon hideHeaderOnExpand>
          <Text textAlign="center" fontWeight="bold" paddingTop="default">
            <MobileConfirmationCollapsibleCardHeader hasBooking={hasBooking} />
          </Text>
        </BottomFixedCollapsibleSection>
      </div>
    </Page>
  )
}

export default ConfirmationComponent
